import { accountApi, contractApi, feeApi, projectApi, reportApi } from '@atheneum/api'

import { downloadFromUrl, tokenStorage, exportDialogMessage } from '@atheneum/api/dist/lib'
import get from 'lodash/get'
import groupBy from 'lodash/groupBy'
import queryString from 'query-string'
import orderBy from 'lodash/orderBy'
import { extendObservable } from 'mobx'
import moment from 'moment'

const defaultModel = {
  page: 1,
  account: null,
  project: null,
  noContract: false,
  startDate: moment().startOf('month').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  contract: null,
  feeType: 'ALL'
}

class GeneralFeesStore {
  constructor() {
    extendObservable(this, {
      loading: null,
      error: false,
      total: null,
      fees: null,
      costs: null,
      model: defaultModel,
      deliverables: []
    })
  }

  getAll = async () => {
    try {
      this.loading = true

      const query = this.getQuery()

      const res = await feeApi.getGeneralFees(query)

      const deliverables = orderBy(
        groupBy(res.rows, 'deliverableId'),
        ['[0].lastFeeDate'],
        ['desc']
      )
        .map(fees => orderBy(fees, ['date_delivered'], ['asc']))
        .map(fees => {
          const deliverable = {
            ...fees[0],
            id: fees[0].deliverableId,
            fees: fees,
            expanded: false
          }
          return deliverable
        })

      this.deliverables.replace(deliverables)

      this.total = res.count
      this.fees = res.fees
      this.costs = res.costs

      this.loading = false
      this.error = false
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error is', err)
      this.error = err
    }
  }

  expand = id => {
    this.deliverables.forEach(d => {
      if (d.id === id) d.expanded = true
    })
  }

  onPageChange = async page => {
    this.model.page = page
    await this.getAll()
  }

  autocompleteClientAccounts = async val =>
    (await accountApi.accountTypeahead(val)).map(a => ({
      val: a.id,
      label: a.companyName
    }))

  autocompleteProjects = async val =>
    (await projectApi.typeahead(val)).map(p => ({
      val: p.id,
      label: p.projectName
    }))

  autocompleteContracts = async val =>
    (await contractApi.typeahead(val)).map(p => ({
      value: p.id,
      label: p.contractReference
    }))

  setVal = (key, val) => {
    if (this.model[key] === val) return
    this.model[key] = val
    this.model.page = 1
    this.getAll()
  }

  export = async props => {
    if (this.exportInProgress) return

    const query = this.getQuery()
    delete query.page

    const token = tokenStorage.get()
    const queryParams = queryString.stringify({ ...query, token })
    const filename = `general_fees_report${moment(query.fromDate).format(
      'YYYY-MM-DD'
    )}-${moment(query.toDate).format('YYYY-MM-DD')}.xlsx`
    const { jobId } = await reportApi.exportGeneralFeesReport(queryParams).catch(() =>
      alert('export failed')
    )
    props.queueProcessorStore.trackJob(jobId, ({ url }) => {
      downloadFromUrl({ url: `${url}?token=${tokenStorage.get()}`, filename })
    })

    this.job = props.queueProcessorStore.jobs[jobId]

    if (this.job && this.job.loading) {
      this.exportInProgress = true
      setTimeout(() => {
        this.exportInProgress = false
      }, 1000)
    }
    await exportDialogMessage({ exportType: 'general fees' })
  }

  clear() {
    this.model = defaultModel
    this.getAll()
  }

  getQuery() {
    return {
      page: this.model.page,
      account:
        get(this.model, 'account.value') || get(this.model, 'account.val'),
      project:
        get(this.model, 'project.value') || get(this.model, 'project.val'),
      contract:
        get(this.model, 'contract.value') || get(this.model, 'contract.val'),
      noContract: get(this.model, 'noContract'),
      startDate: get(this.model, 'startDate'),
      endDate: get(this.model, 'endDate'),
      feeType: get(this.model, 'feeType')
    }
  }
}

export default GeneralFeesStore
